import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
    Box,
    Divider,
    Fab,
    Fade,
    IconButton,
    InputBase,
    Paper,
    Tooltip,
    Typography
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ChatRoundedIcon from '@material-ui/icons/ChatRounded';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import SendRoundedIcon from '@material-ui/icons/SendRounded';
import dompurify from 'dompurify';
import { marked } from 'marked';
import APIHelper from '../common/ApiHelper';
import APIURLConstants from '../common/ApiURLConstants';

const useStyles = makeStyles((theme) => ({
    shell: {
        position: 'fixed',
        right: 24,
        bottom: 24,
        zIndex: 1400,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 16,
        pointerEvents: 'none',
        '& > *': {
            pointerEvents: 'auto',
        },
        [theme.breakpoints.down('xs')]: {
            right: 12,
            left: 12,
            bottom: 12,
        },
    },
    panel: {
        position: 'relative',
        width: 380,
        maxWidth: '100%',
        height: 560,
        maxHeight: 'calc(100vh - 110px)',
        overflow: 'hidden',
        borderRadius: 22,
        border: '1px solid rgba(29, 66, 138, 0.12)',
        boxShadow: '0 24px 54px rgba(6, 22, 52, 0.22)',
        background: 'linear-gradient(180deg, #ffffff 0%, #f4f7fb 100%)',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            height: '70vh',
            maxHeight: '70vh',
            borderRadius: 20,
        },
    },
    header: {
        padding: theme.spacing(2.5, 2.5, 2),
        color: '#ffffff',
        background: 'linear-gradient(135deg, #1d428a 0%, #102957 65%, #c8102e 140%)',
    },
    headerDraggable: {
        cursor: 'move',
        userSelect: 'none',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 28,
        padding: theme.spacing(0, 1.25),
        borderRadius: 999,
        backgroundColor: 'rgba(255,255,255,0.16)',
        border: '1px solid rgba(255,255,255,0.22)',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
    },
    headerTitle: {
        marginTop: theme.spacing(1.5),
        fontWeight: 700,
        fontSize: '1.35rem',
        lineHeight: 1.15,
    },
    headerCopy: {
        marginTop: theme.spacing(1),
        color: 'rgba(255,255,255,0.82)',
        lineHeight: 1.45,
    },
    closeButton: {
        color: '#ffffff',
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    clearButton: {
        color: '#ffffff',
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        padding: theme.spacing(0.5, 1),
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.35)',
        background: 'rgba(255,255,255,0.08)',
        cursor: 'pointer',
        transition: 'background 160ms ease, opacity 160ms ease',
        '&:hover': {
            background: 'rgba(255,255,255,0.16)',
        },
        '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    },
    body: {
        flex: 1,
        minHeight: 0,
        padding: theme.spacing(2),
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        background: 'radial-gradient(circle at top right, rgba(200, 16, 46, 0.06), transparent 32%), transparent',
    },
    bubbleRow: {
        display: 'flex',
    },
    bubbleRowUser: {
        justifyContent: 'flex-end',
    },
    bubble: {
        maxWidth: '85%',
        padding: theme.spacing(1.25, 1.5),
        borderRadius: 18,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        boxShadow: '0 10px 25px rgba(16, 41, 87, 0.08)',
    },
    bubbleContent: {
        '& p': {
            margin: 0,
        },
        '& p + p': {
            marginTop: theme.spacing(1),
        },
        '& ul, & ol': {
            margin: theme.spacing(1, 0, 0, 2.5),
            padding: 0,
        },
        '& li + li': {
            marginTop: theme.spacing(0.5),
        },
        '& a': {
            color: 'inherit',
            textDecoration: 'underline',
        },
        '& strong': {
            fontWeight: 700,
        },
    },
    bubbleAssistant: {
        color: '#102957',
        backgroundColor: '#ffffff',
        borderTopLeftRadius: 6,
    },
    thinkingBubble: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.8),
        color: '#30486f',
        fontStyle: 'italic',
    },
    thinkingDots: {
        letterSpacing: 2,
        animation: '$blinkDots 1.1s infinite steps(1, end)',
    },
    '@keyframes blinkDots': {
        '0%, 20%': {
            opacity: 0.2,
        },
        '40%': {
            opacity: 0.45,
        },
        '60%': {
            opacity: 0.7,
        },
        '80%, 100%': {
            opacity: 1,
        },
    },
    bubbleUser: {
        color: '#ffffff',
        background: 'linear-gradient(135deg, #1d428a 0%, #315fb8 100%)',
        borderTopRightRadius: 6,
    },
    helperCard: {
        padding: theme.spacing(1.5),
        borderRadius: 16,
        backgroundColor: 'rgba(29, 66, 138, 0.06)',
        border: '1px solid rgba(29, 66, 138, 0.08)',
        color: '#102957',
    },
    promptRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    promptChip: {
        cursor: 'pointer',
        padding: theme.spacing(0.875, 1.25),
        borderRadius: 999,
        backgroundColor: '#ffffff',
        border: '1px solid rgba(29, 66, 138, 0.14)',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 8px 18px rgba(16, 41, 87, 0.08)',
            borderColor: 'rgba(29, 66, 138, 0.28)',
        },
    },
    composerWrap: {
        padding: theme.spacing(1.5, 2),
        backgroundColor: '#ffffff',
    },
    composer: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 0.75, 0.75, 1.5),
        borderRadius: 18,
        border: '1px solid rgba(29, 66, 138, 0.12)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
    },
    input: {
        flex: 1,
        fontSize: 14,
        lineHeight: 1.5,
        minHeight: 24,
    },
    sendButton: {
        width: 42,
        height: 42,
        borderRadius: 14,
        color: '#ffffff',
        background: 'linear-gradient(135deg, #c8102e 0%, #e13d5c 100%)',
        '&:hover': {
            background: 'linear-gradient(135deg, #b10d28 0%, #cf3452 100%)',
        },
        '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.65)',
            background: '#c7d2e6',
        },
    },
    footerNote: {
        marginTop: theme.spacing(1),
        color: '#5a6780',
        fontSize: 12,
    },
    fab: {
        width: 64,
        height: 64,
        boxShadow: '0 18px 34px rgba(16, 41, 87, 0.28)',
        background: 'linear-gradient(135deg, #1d428a 0%, #c8102e 100%)',
        color: '#ffffff',
        '&:hover': {
            background: 'linear-gradient(135deg, #17356f 0%, #ae0f28 100%)',
        },
        [theme.breakpoints.down('xs')]: {
            width: 58,
            height: 58,
            alignSelf: 'flex-end',
        },
    },
    fabIcon: {
        fontSize: 30,
    },
    resizeHandle: {
        position: 'absolute',
        zIndex: 3,
    },
    resizeHandleTop: {
        top: -4,
        left: 12,
        right: 12,
        height: 10,
        cursor: 'ns-resize',
    },
    resizeHandleBottom: {
        bottom: -4,
        left: 12,
        right: 12,
        height: 10,
        cursor: 'ns-resize',
    },
    resizeHandleLeft: {
        left: -4,
        top: 12,
        bottom: 12,
        width: 10,
        cursor: 'ew-resize',
    },
    resizeHandleRight: {
        right: -4,
        top: 12,
        bottom: 12,
        width: 10,
        cursor: 'ew-resize',
    },
    resizeHandleTopLeft: {
        top: -4,
        left: -4,
        width: 16,
        height: 16,
        cursor: 'nwse-resize',
    },
    resizeHandleTopRight: {
        top: -4,
        right: -4,
        width: 16,
        height: 16,
        cursor: 'nesw-resize',
    },
    resizeHandleBottomLeft: {
        bottom: -4,
        left: -4,
        width: 16,
        height: 16,
        cursor: 'nesw-resize',
    },
    resizeHandleBottomRight: {
        position: 'absolute',
        right: 0,
        bottom: 0,
        width: 20,
        height: 20,
        cursor: 'nwse-resize',
        background: 'linear-gradient(135deg, transparent 46%, rgba(16, 41, 87, 0.34) 46%, rgba(16, 41, 87, 0.34) 54%, transparent 54%)',
        zIndex: 2,
    },
}));

const starterPrompts = [
    'Summarize this page',
    'Help me find a feature',
    'Draft a reply',
];

const initialMessages = [
    {
        id: 'welcome',
        role: 'assistant',
        content: 'Ask anything about officiating content, evaluation result for any prospect.'
    }
];

function createAssistantReply(userText) {
    return `Received: "${userText}"\n\nThere is an issue with the AI service, try again later.`;
}

function getAssistantMessageFromResponse(response) {
    if (typeof response === 'string') {
        return response;
    }

    if (!response || typeof response !== 'object') {
        return '';
    }

    return (
       (response.data.answer && typeof response.data.answer === 'string' ? response.data.answer : '')
    );
}

function getSessionIdFromResponse(response) {
    if (!response || typeof response !== 'object') {
        return '';
    }

    return response.data.sessionId  || '';
}

function getAssistantMessageMarkup(content) {
    return {
        __html: dompurify.sanitize(marked.parse(content || '', { breaks: true })),
    };
}

export default function AIChatWidget() {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const [messages, setMessages] = useState(initialMessages);
    const [isAsking, setIsAsking] = useState(false);
    const [sessionId, setSessionId] = useState('');
    const [panelSize, setPanelSize] = useState({ width: 380, height: 560 });
    const [widgetOffset, setWidgetOffset] = useState({ x: 0, y: 0 });
    const [fabOffset, setFabOffset] = useState({ x: 0, y: 0 });
    const bodyRef = useRef(null);
    const panelRef = useRef(null);
    const fabRef = useRef(null);
    const resizingRef = useRef(null);
    const draggingRef = useRef(null);
    const fabDragRef = useRef(null);

    const canSend = useMemo(() => draft.trim().length > 0 && !isAsking, [draft, isAsking]);

    useEffect(() => {
        if (bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [messages, isOpen]);

    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', handleResizeMouseMove);
            window.removeEventListener('mouseup', handleResizeMouseUp);
            window.removeEventListener('mousemove', handleDragMouseMove);
            window.removeEventListener('mouseup', handleDragMouseUp);
            window.removeEventListener('mousemove', handleFabDragMouseMove);
            window.removeEventListener('mouseup', handleFabDragMouseUp);
        };
    }, []);

    const handleFabDragMouseMove = (event) => {
        if (!fabDragRef.current) {
            return;
        }
        const deltaX = event.clientX - fabDragRef.current.startX;
        const deltaY = event.clientY - fabDragRef.current.startY;
        if (!fabDragRef.current.moved && Math.abs(deltaX) + Math.abs(deltaY) > 4) {
            fabDragRef.current.moved = true;
        }
        const nextX = Math.max(fabDragRef.current.minDeltaX, Math.min(fabDragRef.current.maxDeltaX, deltaX));
        const nextY = Math.max(fabDragRef.current.minDeltaY, Math.min(fabDragRef.current.maxDeltaY, deltaY));
        setFabOffset({
            x: fabDragRef.current.startOffsetX + nextX,
            y: fabDragRef.current.startOffsetY + nextY,
        });
    };

    const handleFabDragMouseUp = () => {
        window.removeEventListener('mousemove', handleFabDragMouseMove);
        window.removeEventListener('mouseup', handleFabDragMouseUp);
        // Delay clearing so click handler can check `moved` flag
        setTimeout(() => { fabDragRef.current = null; }, 0);
    };

    const handleFabMouseDown = (event) => {
        if (isMobile || !fabRef.current || event.button !== 0) {
            return;
        }
        const rect = fabRef.current.getBoundingClientRect();
        fabDragRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            startOffsetX: fabOffset.x,
            startOffsetY: fabOffset.y,
            minDeltaX: -rect.left,
            maxDeltaX: window.innerWidth - rect.right,
            minDeltaY: -rect.top,
            maxDeltaY: window.innerHeight - rect.bottom,
            moved: false,
        };
        window.addEventListener('mousemove', handleFabDragMouseMove);
        window.addEventListener('mouseup', handleFabDragMouseUp);
    };

    const handleFabClick = () => {
        if (fabDragRef.current && fabDragRef.current.moved) {
            return;
        }
        setIsOpen((current) => !current);
    };

    const handleDragMouseMove = (event) => {
        if (!draggingRef.current) {
            return;
        }

        const deltaX = event.clientX - draggingRef.current.startX;
        const deltaY = event.clientY - draggingRef.current.startY;
        const nextDeltaX = Math.max(draggingRef.current.minDeltaX, Math.min(draggingRef.current.maxDeltaX, deltaX));
        const nextDeltaY = Math.max(draggingRef.current.minDeltaY, Math.min(draggingRef.current.maxDeltaY, deltaY));

        setWidgetOffset({
            x: draggingRef.current.startOffsetX + nextDeltaX,
            y: draggingRef.current.startOffsetY + nextDeltaY,
        });
    };

    const handleDragMouseUp = () => {
        draggingRef.current = null;
        window.removeEventListener('mousemove', handleDragMouseMove);
        window.removeEventListener('mouseup', handleDragMouseUp);
    };

    const handleDragMouseDown = (event) => {
        if (isMobile || !panelRef.current) {
            return;
        }

        event.preventDefault();

        const rect = panelRef.current.getBoundingClientRect();

        draggingRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            startOffsetX: widgetOffset.x,
            startOffsetY: widgetOffset.y,
            minDeltaX: -rect.left,
            maxDeltaX: window.innerWidth - rect.right,
            minDeltaY: -rect.top,
            maxDeltaY: window.innerHeight - rect.bottom,
        };

        window.addEventListener('mousemove', handleDragMouseMove);
        window.addEventListener('mouseup', handleDragMouseUp);
    };

    const handleResizeMouseMove = (event) => {
        if (!resizingRef.current) {
            return;
        }

        const minWidth = 320;
        const minHeight = 420;
        const {
            direction,
            startX,
            startY,
            startWidth,
            startHeight,
            startRect,
        } = resizingRef.current;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;

        const maxWidthFromLeftEdge = Math.max(minWidth, window.innerWidth - startRect.left);
        const maxWidthFromRightEdge = Math.max(minWidth, startRect.right);
        const maxHeightFromTopEdge = Math.max(minHeight, window.innerHeight - startRect.top);
        const maxHeightFromBottomEdge = Math.max(minHeight, startRect.bottom);

        let nextWidth = startWidth;
        let nextHeight = startHeight;

        if (direction.includes('right')) {
            nextWidth = Math.min(maxWidthFromLeftEdge, Math.max(minWidth, startWidth + deltaX));
        }

        if (direction.includes('left')) {
            nextWidth = Math.min(maxWidthFromRightEdge, Math.max(minWidth, startWidth - deltaX));
        }

        if (direction.includes('bottom')) {
            nextHeight = Math.min(maxHeightFromTopEdge, Math.max(minHeight, startHeight + deltaY));
        }

        if (direction.includes('top')) {
            nextHeight = Math.min(maxHeightFromBottomEdge, Math.max(minHeight, startHeight - deltaY));
        }

        setPanelSize({
            width: nextWidth,
            height: nextHeight,
        });
    };

    const handleResizeMouseUp = () => {
        resizingRef.current = null;
        window.removeEventListener('mousemove', handleResizeMouseMove);
        window.removeEventListener('mouseup', handleResizeMouseUp);
    };

    const handleResizeMouseDown = (direction) => (event) => {
        if (isMobile) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const rect = panelRef.current ? panelRef.current.getBoundingClientRect() : null;

        if (!rect) {
            return;
        }

        resizingRef.current = {
            direction,
            startX: event.clientX,
            startY: event.clientY,
            startWidth: panelSize.width,
            startHeight: panelSize.height,
            startRect: rect,
        };

        window.addEventListener('mousemove', handleResizeMouseMove);
        window.addEventListener('mouseup', handleResizeMouseUp);
    };

    const submitMessage = async (value) => {
        const trimmedValue = value.trim();

        if (!trimmedValue || isAsking) {
            return;
        }

        const userMessageId = `user-${Date.now()}`;

        setMessages((currentMessages) => [
            ...currentMessages,
            {
                id: userMessageId,
                role: 'user',
                content: trimmedValue,
            }
        ]);
        setDraft('');
        setIsOpen(true);

        try {
            setIsAsking(true);
            const payload = {
                question: trimmedValue,
            };

            if (sessionId) {
                payload.sessionid = sessionId;
            }
            const response = await APIHelper.post(APIURLConstants.CHAT_ASK_QUESTION, payload);
            const assistantReply = getAssistantMessageFromResponse(response);

            const nextSessionId = getSessionIdFromResponse(response);

            if (nextSessionId) {
                setSessionId(nextSessionId);
            }
            setMessages((currentMessages) => [
                ...currentMessages,
                {
                    id: `assistant-${Date.now() + 1}`,
                    role: 'assistant',
                    content: assistantReply || createAssistantReply(trimmedValue),
                }
            ]);
        } catch (error) {
            setMessages((currentMessages) => [
                ...currentMessages,
                {
                    id: `assistant-error-${Date.now()}`,
                    role: 'assistant',
                    content: 'Unable to get AI response right now. Please try again.',
                }
            ]);
        } finally {
            setIsAsking(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submitMessage(draft);
        }
    };

    const handleClearChat = () => {
        setSessionId('');
        setMessages(initialMessages);
        setDraft('');
    };

    const panelStyle = isMobile
        ? undefined
        : {
            width: panelSize.width,
            height: panelSize.height,
            transform: `translate(${widgetOffset.x}px, ${widgetOffset.y}px)`,
        };

    return (
        <div className={classes.shell}>
            <Fade in={isOpen} unmountOnExit>
                <Paper elevation={0} className={classes.panel} style={panelStyle} ref={panelRef}>
                    <div className={clsx(classes.header, { [classes.headerDraggable]: !isMobile })} onMouseDown={handleDragMouseDown}>
                        <div className={classes.headerRow}>
                            <Box>
                                <span className={classes.headerTitle} variant="h6">AI Assistant</span>
                                {/* <Typography className={classes.headerTitle} variant="h6">
                                    Ask, summarize, or draft from anywhere.
                                </Typography>
                                <Typography className={classes.headerCopy} variant="body2">
                                    Open the assistant from the floating button and use it as a starting point for a real AI workflow.
                                </Typography> */}
                            </Box>
                            <div className={classes.headerActions} onMouseDown={(event) => event.stopPropagation()}>
                                <button
                                    type="button"
                                    className={classes.clearButton}
                                    onClick={handleClearChat}
                                    disabled={isAsking}
                                >
                                    Clear
                                </button>
                                <IconButton className={classes.closeButton} onClick={() => setIsOpen(false)}>
                                    <CloseRoundedIcon />
                                </IconButton>
                            </div>
                        </div>
                    </div>
                    <div className={classes.body} ref={bodyRef}>
                        {/* <div className={classes.helperCard}>
                            <Typography variant="subtitle2">Try a prompt</Typography>
                            <div className={classes.promptRow}>
                                {starterPrompts.map((prompt) => (
                                    <Typography
                                        component="button"
                                        type="button"
                                        key={prompt}
                                        className={classes.promptChip}
                                        onClick={() => submitMessage(prompt)}
                                    >
                                        {prompt}
                                    </Typography>
                                ))}
                            </div>
                        </div> */}
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={clsx(classes.bubbleRow, {
                                    [classes.bubbleRowUser]: message.role === 'user',
                                })}
                            >
                                <div
                                    className={clsx(classes.bubble, {
                                        [classes.bubbleAssistant]: message.role === 'assistant',
                                        [classes.bubbleUser]: message.role === 'user',
                                    })}
                                >
                                    {message.role === 'assistant' ? (
                                        <div
                                            className={classes.bubbleContent}
                                            dangerouslySetInnerHTML={getAssistantMessageMarkup(message.content)}
                                        />
                                    ) : (
                                        <Typography variant="body2" className={classes.bubbleContent}>{message.content}</Typography>
                                    )}
                                </div>
                            </div>
                        ))}
                        {isAsking && (
                            <div className={classes.bubbleRow}>
                                <div className={clsx(classes.bubble, classes.bubbleAssistant, classes.thinkingBubble)}>
                                    <Typography variant="body2">Thinking</Typography>
                                    <Typography variant="body2" className={classes.thinkingDots}>...</Typography>
                                </div>
                            </div>
                        )}
                    </div>
                    <Divider />
                    <div className={classes.composerWrap}>
                        <Paper elevation={0} className={classes.composer}>
                            <InputBase
                                multiline
                                maxRows={4}
                                placeholder={isAsking ? 'AI is typing...' : 'Ask the AI assistant...'}
                                value={draft}
                                onChange={(event) => setDraft(event.target.value)}
                                onKeyDown={handleKeyDown}
                                className={classes.input}
                                disabled={isAsking}
                            />
                            <IconButton
                                className={classes.sendButton}
                                disabled={!canSend}
                                onClick={() => submitMessage(draft)}
                            >
                                <SendRoundedIcon fontSize="small" />
                            </IconButton>
                        </Paper>
                       
                    </div>
                    {!isMobile && (
                        <>
                            <div className={clsx(classes.resizeHandle, classes.resizeHandleTop)} onMouseDown={handleResizeMouseDown('top')} role="presentation" aria-hidden="true" />
                            <div className={clsx(classes.resizeHandle, classes.resizeHandleBottom)} onMouseDown={handleResizeMouseDown('bottom')} role="presentation" aria-hidden="true" />
                            <div className={clsx(classes.resizeHandle, classes.resizeHandleLeft)} onMouseDown={handleResizeMouseDown('left')} role="presentation" aria-hidden="true" />
                            <div className={clsx(classes.resizeHandle, classes.resizeHandleRight)} onMouseDown={handleResizeMouseDown('right')} role="presentation" aria-hidden="true" />
                            <div className={clsx(classes.resizeHandle, classes.resizeHandleTopLeft)} onMouseDown={handleResizeMouseDown('top-left')} role="presentation" aria-hidden="true" />
                            <div className={clsx(classes.resizeHandle, classes.resizeHandleTopRight)} onMouseDown={handleResizeMouseDown('top-right')} role="presentation" aria-hidden="true" />
                            <div className={clsx(classes.resizeHandle, classes.resizeHandleBottomLeft)} onMouseDown={handleResizeMouseDown('bottom-left')} role="presentation" aria-hidden="true" />
                            <div className={clsx(classes.resizeHandle, classes.resizeHandleBottomRight)} onMouseDown={handleResizeMouseDown('bottom-right')} role="presentation" aria-hidden="true" />
                        </>
                    )}
                </Paper>
            </Fade>

            <Tooltip title={isOpen ? 'Hide AI assistant' : 'Open AI assistant (drag to move)'} placement="left">
                <Fab
                    ref={fabRef}
                    className={classes.fab}
                    onMouseDown={handleFabMouseDown}
                    onClick={handleFabClick}
                    style={isMobile ? undefined : { transform: `translate(${fabOffset.x}px, ${fabOffset.y}px)` }}
                >
                    <ChatRoundedIcon className={classes.fabIcon} />
                </Fab>
            </Tooltip>
        </div>
    );
}